import { useFormikContext, Field } from 'formik';
import { useCallback } from 'react';

import {
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  OutlineCircleInfoIcon,
  RadioGroup,
  Radio,
  TableContainer,
  TableV2,
  Tbody,
  Text,
  Th,
  Td,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@frontend/components';

import { ProfitItemRow, type ProfitItemRowProps } from './ProfitItemRow';
import type { FormValues } from './types';

interface SharedProfitItemsTableProps {
  serverErrors: Map<number, string>;
  onClearServerError: (profitItemDefId: number) => void;
  onClearAllServerErrors: () => void;
}

export const SharedProfitItemsTable = ({
  serverErrors,
  onClearServerError,
  onClearAllServerErrors,
}: SharedProfitItemsTableProps) => {
  const { setFieldValue, values } = useFormikContext<FormValues>();

  const editSharedProfitItem = useCallback<ProfitItemRowProps['onEdit']>(
    (id, data) => {
      const newSharedProfitItems = values.sharedProfitItems.map(
        (sharedProfitItem) =>
          sharedProfitItem.id === id
            ? { ...sharedProfitItem, ...data }
            : sharedProfitItem,
      );
      setFieldValue('sharedProfitItems', newSharedProfitItems);

      const edited = values.sharedProfitItems.find((p) => p.id === id);
      if (edited) {
        onClearServerError(edited.profitItemDefId);
      }
    },
    [setFieldValue, values.sharedProfitItems, onClearServerError],
  );

  const onChangeUpdateType = useCallback(
    (val: string) => {
      setFieldValue('updateType', val);
      onClearAllServerErrors();
    },
    [setFieldValue, onClearAllServerErrors],
  );

  const onChangeApplyOverrides = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue('applyOverrides', e.target.checked);
    },
    [setFieldValue],
  );

  return (
    <VStack>
      <FormControl isRequired>
        <FormLabel>Adjust Percentages</FormLabel>
        <Field name="updateType" as={RadioGroup} onChange={onChangeUpdateType}>
          <HStack spacing={1.5}>
            <Radio value="actual">Set to</Radio>
            <Radio value="increase">Increase by</Radio>
            <Radio value="decrease">Decrease by</Radio>
          </HStack>
        </Field>
      </FormControl>
      <TableContainer flexShrink={0}>
        <TableV2>
          <Thead>
            <Tr>
              <Th>Pricing</Th>
              <Th w={24} pr={2} pl={0.25} textAlign="right">
                Percent (All Qtys)
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {!values.sharedProfitItems.length && (
              <Tr>
                <Td colSpan={2} textAlign="center" fontWeight="heavy">
                  No pricing items
                </Td>
              </Tr>
            )}
            {values.sharedProfitItems.map((sharedProfitItem) => (
              <ProfitItemRow
                key={sharedProfitItem.id}
                profitItem={sharedProfitItem}
                onEdit={editSharedProfitItem}
                error={serverErrors.get(sharedProfitItem.profitItemDefId)}
              />
            ))}
          </Tbody>
        </TableV2>
      </TableContainer>
      <FormControl>
        <Field name="action" as={Checkbox} onChange={onChangeApplyOverrides}>
          <Flex gap={0.5} align="center">
            <Text>Apply to overrides</Text>
            <Tooltip
              placement="bottom"
              label="If checked, changes will be applied to overrides, if they exist"
            >
              <OutlineCircleInfoIcon
                boxSize="1.25rem"
                color="feature.secondary"
              />
            </Tooltip>
          </Flex>
        </Field>
      </FormControl>
    </VStack>
  );
};
